// @flow

import { forEach, mapKeys, pickBy, snakeCase, startCase } from 'lodash/fp';

import { loadScript } from '@zapier/common-utils';
import {
  getElementPlaceholder,
  getHostedFieldSelector,
} from '@zapier/payment/src/components/CreditCardForm/HostedFields/utils';

import type {
  HostedFieldAdditionalData,
  HostedFieldBackendInterface,
  HostedFieldToken,
  HostedFieldType,
  HostedFieldValue,
} from '@zapier/payment/src/types';

class StripeBackend implements HostedFieldBackendInterface {
  stripe: any;
  elements: any;

  static instance: ?StripeBackend = null;
  static isLoading: boolean = false;
  static elementsRegistry: Map<HostedFieldType, any> = new Map();

  constructor(stripe: any) {
    this.stripe = stripe;
    this.elements = stripe.elements({
      fonts: [
        {
          cssSrc: 'https://fonts.googleapis.com/css?family=Open+Sans',
        },
      ],
    });
  }

  createFields(
    style: any,
    onChange: (HostedFieldType, HostedFieldValue) => void
  ) {
    const types = ['number', 'expiry', 'cvc'];

    forEach(type => {
      const element = this.elements.create(`card${startCase(type)}`, {
        style: { base: style },
        placeholder: getElementPlaceholder(type),
      });

      StripeBackend.elementsRegistry.set(type, element);

      element.addEventListener('change', event => {
        onChange(type, {
          isEmpty: event.empty,
          originalError: event.error ? event.error.message : '',
        });
      });

      element.mount(getHostedFieldSelector(type));
    }, types);
  }

  destroyFields() {
    const types = ['number', 'expiry', 'cvc'];

    forEach(type => {
      const element = StripeBackend.elementsRegistry.get(type);

      if (element) {
        element.destroy();
        StripeBackend.elementsRegistry.delete(type);
      }
    }, types);
  }

  async createToken(
    additionalData: HostedFieldAdditionalData
  ): Promise<HostedFieldToken> {
    const firstElement = StripeBackend.elementsRegistry.values().next().value;
    const cleanData = pickBy(Boolean, additionalData);
    const data = mapKeys(snakeCase, cleanData);

    const { error, token } = await this.stripe.createToken(firstElement, data);

    if (error) {
      throw new Error(error.message);
    }

    return {
      token: token.id,
      details: token.card,
    };
  }

  static async create(apiKey: string): Promise<HostedFieldBackendInterface> {
    if (!StripeBackend.instance) {
      if (!window.Stripe && !StripeBackend.isLoading) {
        StripeBackend.isLoading = true;
        await loadScript('https://js.stripe.com/v3/');
      }

      StripeBackend.instance = new StripeBackend(window.Stripe(apiKey));
    }

    return StripeBackend.instance;
  }
}

export default StripeBackend.create;
